import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TiptapImage from "@tiptap/extension-image";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link2,
  List,
  ListOrdered,
  Image as ImageIcon,
  Loader2,
  Type,
  Highlighter,
  X,
} from "lucide-react";
import { API_URL, STORAGE } from "@/_config";

// ---------------------------------------------------------------------------
// Custom FontSize extension
// ---------------------------------------------------------------------------
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el: HTMLElement) => el.style.fontSize || null,
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setFontSize: (size: string) => ({ chain }: any) =>
        chain().setMark("textStyle", { fontSize: size }).run(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      unsetFontSize: () => ({ chain }: any) =>
        chain().setMark("textStyle", { fontSize: null }).run(),
    };
  },
});

const FONT_SIZES = ["12px", "13px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"];

const TEXT_COLORS = [
  "#000000", "#374151", "#6b7280", "#9ca3af",
  "#dc2626", "#ea580c", "#d97706", "#16a34a",
  "#0284c7", "#7c3aed", "#db2777", "#0f766e",
];

const HIGHLIGHT_COLORS = [
  "#fef08a", "#bbf7d0", "#bfdbfe", "#fecdd3",
  "#fed7aa", "#e9d5ff", "#99f6e4", "#fde68a",
  "transparent",
];

// ---------------------------------------------------------------------------
// ColorPicker
// ---------------------------------------------------------------------------
interface ColorPickerProps {
  colors: string[];
  onSelect: (color: string) => void;
  onClose: () => void;
  customLabel?: string;
}

function ColorPicker({ colors, onSelect, onClose, customLabel = "Custom" }: ColorPickerProps) {
  const customRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="color-picker-popover" ref={ref}>
      <div className="color-picker-grid">
        {colors.map((c) => (
          <button
            key={c}
            type="button"
            className="color-swatch"
            style={{
              background: c === "transparent"
                ? "linear-gradient(135deg, #fff 45%, #f87171 45%, #f87171 55%, #fff 55%)"
                : c,
              border: c === "transparent" ? "1px solid #e5e7eb" : "1px solid rgba(0,0,0,0.12)",
            }}
            title={c === "transparent" ? "Remove highlight" : c}
            onClick={() => { onSelect(c); onClose(); }}
          />
        ))}
      </div>
      <div className="color-picker-custom">
        <span className="color-picker-custom-label">{customLabel}</span>
        <input
          ref={customRef}
          type="color"
          className="color-picker-native"
          onChange={(e) => onSelect(e.target.value)}
        />
        <button
          type="button"
          className="color-picker-custom-btn"
          onClick={() => customRef.current?.click()}
        >
          Pick color
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageInsertDialog
// ---------------------------------------------------------------------------
interface ImageInsertDialogProps {
  onInsert: (src: string, alt: string) => void;
  onClose: () => void;
}

function ImageInsertDialog({ onInsert, onClose }: ImageInsertDialogProps) {
  const [mode, setMode] = useState<"url" | "upload">("url");
  const [src, setSrc] = useState("");
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    setSrc("");
    try {
      const raw = localStorage.getItem(STORAGE);
      const stored = raw ? JSON.parse(raw) : null;
      const token = stored?.token ?? stored?.access_token ?? "";
      const form = new FormData();
      form.append("image", file);
      const res = await fetch(API_URL.EMAIL_IMAGES, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setUploadError(err?.message ?? "Upload failed. Please try again.");
        return;
      }
      const data = await res.json();
      setSrc(data?.data?.url ?? "");
    } catch {
      setUploadError("Upload failed. Please check your connection.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="img-dialog-overlay">
      <div className="img-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="img-dialog-header">
          <span className="img-dialog-title">Insert Image</span>
          <button type="button" className="img-dialog-close" onClick={onClose}>
            <X size={15} />
          </button>
        </div>
        <div className="img-dialog-tabs">
          <button type="button" className={`img-dialog-tab${mode === "url" ? " active" : ""}`}
            onClick={() => { setMode("url"); setSrc(""); }}>URL</button>
          <button type="button" className={`img-dialog-tab${mode === "upload" ? " active" : ""}`}
            onClick={() => { setMode("upload"); setSrc(""); }}>Upload</button>
        </div>
        <div className="img-dialog-body">
          {mode === "url" ? (
            <input type="url" className="img-dialog-input" placeholder="https://example.com/image.jpg"
              value={src} onChange={(e) => setSrc(e.target.value)} autoFocus />
          ) : (
            <>
              <div className={`img-upload-area${src ? " img-upload-area--has-file" : ""}`}
                onClick={() => !uploading && fileRef.current?.click()}>
                {uploading ? (
                  <div className="img-upload-placeholder">
                    <Loader2 size={22} className="img-upload-spinner" />
                    <span>Uploading…</span>
                  </div>
                ) : src ? (
                  <img src={src} alt="preview" className="img-upload-preview" />
                ) : (
                  <div className="img-upload-placeholder">
                    <ImageIcon size={26} />
                    <span>Click to choose an image</span>
                    <span className="img-upload-hint">PNG, JPG, GIF, WebP — max 5 MB</span>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: "none" }} onChange={handleFileChange} />
              </div>
              {uploadError && <p style={{ margin: 0, fontSize: 12, color: "#b91c1c" }}>{uploadError}</p>}
            </>
          )}
          <input type="text" className="img-dialog-input" placeholder="Alt text (optional)"
            value={alt} onChange={(e) => setAlt(e.target.value)} />
        </div>
        <div className="img-dialog-footer">
          <button type="button" className="img-dialog-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="img-dialog-insert"
            onClick={() => { if (src.trim()) { onInsert(src.trim(), alt.trim()); onClose(); } }}
            disabled={!src.trim() || uploading}>Insert Image</button>
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return <span className="email-toolbar-divider" />;
}

// ---------------------------------------------------------------------------
// SupportEmailEditor — Tiptap editor without AI / variables / code-view
// ---------------------------------------------------------------------------
interface SupportEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

export default function SupportEmailEditor({
  value,
  onChange,
  disabled = false,
  hasError = false,
}: SupportEmailEditorProps) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      TiptapImage.configure({ inline: false, allowBase64: true }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder: "Describe your issue in detail…" }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!editor) return;
    editor.setOptions({ editable: !disabled });
  }, [editor, disabled]);

  const handleLinkToggle = () => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt("Enter URL", "https://");
      if (url) {
        editor.chain().focus().setLink({ href: url.trim() }).run();
      }
    }
  };

  const currentFontSize = editor?.getAttributes("textStyle")?.fontSize ?? "";

  return (
    <div className={`email-editor-wrap${hasError ? " email-editor-wrap--error" : ""}`}>
      <EditorContent editor={editor} />

      <div className="email-editor-toolbar">
        <div className="email-editor-toolbar-left">
          <button type="button" className={`etb${editor?.isActive("bold") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBold().run()} disabled={disabled} title="Bold">
            <Bold size={14} />
          </button>
          <button type="button" className={`etb${editor?.isActive("italic") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={disabled} title="Italic">
            <Italic size={14} />
          </button>
          <button type="button" className={`etb${editor?.isActive("underline") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleUnderline().run()} disabled={disabled} title="Underline">
            <UnderlineIcon size={14} />
          </button>

          <Divider />

          <select className="etb-select" value={currentFontSize}
            onChange={(e) => {
              if (!editor) return;
              if (e.target.value) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).setFontSize(e.target.value).run();
              } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor.chain().focus() as any).unsetFontSize().run();
              }
            }}
            disabled={disabled} title="Font Size">
            <option value="">Size</option>
            {FONT_SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
          </select>

          <div className="etb-color-wrap">
            <button type="button" className={`etb etb--color${showColorPicker ? " etb--active" : ""}`}
              onClick={() => { setShowColorPicker((v) => !v); setShowHighlightPicker(false); }}
              disabled={disabled} title="Text Color">
              <Type size={14} />
              <span className="etb-color-bar"
                style={{ background: editor?.getAttributes("textStyle")?.color || "#000" }} />
            </button>
            {showColorPicker && (
              <ColorPicker colors={TEXT_COLORS}
                onSelect={(color) => editor?.chain().focus().setColor(color).run()}
                onClose={() => setShowColorPicker(false)} customLabel="Custom color" />
            )}
          </div>

          <div className="etb-color-wrap">
            <button type="button" className={`etb etb--color${showHighlightPicker ? " etb--active" : ""}`}
              onClick={() => { setShowHighlightPicker((v) => !v); setShowColorPicker(false); }}
              disabled={disabled} title="Highlight Color">
              <Highlighter size={14} />
              <span className="etb-color-bar"
                style={{ background: (editor?.isActive("highlight") && editor.getAttributes("highlight")?.color) || "#fef08a" }} />
            </button>
            {showHighlightPicker && (
              <ColorPicker colors={HIGHLIGHT_COLORS}
                onSelect={(color) => {
                  if (color === "transparent") editor?.chain().focus().unsetHighlight().run();
                  else editor?.chain().focus().setHighlight({ color }).run();
                }}
                onClose={() => setShowHighlightPicker(false)} customLabel="Custom highlight" />
            )}
          </div>

          <Divider />

          <button type="button" className={`etb${editor?.isActive("bulletList") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()} disabled={disabled} title="Bullet List">
            <List size={14} />
          </button>
          <button type="button" className={`etb${editor?.isActive("orderedList") ? " etb--active" : ""}`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()} disabled={disabled} title="Numbered List">
            <ListOrdered size={14} />
          </button>

          <Divider />

          <button type="button" className={`etb${editor?.isActive("link") ? " etb--active" : ""}`}
            onClick={handleLinkToggle} disabled={disabled} title="Insert Link">
            <Link2 size={14} />
          </button>
          <button type="button" className="etb"
            onClick={() => setShowImageDialog(true)} disabled={disabled} title="Insert Image">
            <ImageIcon size={14} />
          </button>
        </div>
      </div>

      {showImageDialog && (
        <ImageInsertDialog
          onInsert={(src, alt) => editor?.chain().focus().setImage({ src, alt }).run()}
          onClose={() => setShowImageDialog(false)}
        />
      )}

      <style>{`
        .email-editor-wrap .ProseMirror {
          min-height: 220px; padding: 14px 16px; outline: none;
          font-size: 14px; line-height: 1.7; color: var(--gray-800, #1a202c);
        }
        .email-editor-wrap .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder); color: var(--gray-400, #a0aec0);
          pointer-events: none; float: left; height: 0;
        }
        .email-editor-wrap {
          border: 1px solid var(--gray-200, #e2e8f0);
          border-radius: 8px; overflow: hidden; background: #fff;
        }
        .email-editor-wrap--error { border-color: #e53e3e; }
        .email-editor-wrap .ProseMirror ul { padding-left: 22px; list-style-type: disc; }
        .email-editor-wrap .ProseMirror ol { padding-left: 22px; list-style-type: decimal; }
        .email-editor-wrap .ProseMirror a { color: #0284c7; text-decoration: underline; }
        .email-editor-wrap .ProseMirror strong { font-weight: 600; }
        .email-editor-wrap .ProseMirror em { font-style: italic; }
        .email-editor-wrap .ProseMirror u { text-decoration: underline; }
        .email-editor-wrap .ProseMirror img { max-width: 100%; height: auto; display: block; margin: 8px 0; border-radius: 4px; }
        .email-editor-wrap .ProseMirror mark { border-radius: 2px; padding: 0 2px; }
        .email-editor-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 6px 10px; border-top: 1px solid #f1f5f9;
          background: #f8fafc; gap: 2px; flex-wrap: wrap;
        }
        .email-editor-toolbar-left { display: flex; align-items: center; gap: 1px; flex-wrap: wrap; }
        .email-toolbar-divider { display: inline-block; width: 1px; height: 18px; background: #e2e8f0; margin: 0 4px; flex-shrink: 0; }
        .etb {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; border-radius: 5px; border: none;
          background: transparent; cursor: pointer; color: #64748b;
          transition: background 0.12s, color 0.12s; flex-shrink: 0;
        }
        .etb:hover:not(:disabled) { background: #e2e8f0; color: #1e293b; }
        .etb--active { background: #dbeafe; color: #1d4ed8; }
        .etb--active:hover:not(:disabled) { background: #bfdbfe; }
        .etb:disabled { opacity: 0.4; cursor: not-allowed; }
        .etb--color { flex-direction: column; gap: 0; width: 28px; height: 28px; padding: 3px 4px 2px; }
        .etb-color-bar { display: block; width: 14px; height: 3px; border-radius: 2px; margin-top: 1px; flex-shrink: 0; }
        .etb-select {
          height: 28px; padding: 0 4px; font-size: 12px; border: 1px solid #e2e8f0;
          border-radius: 5px; background: #fff; color: #374151; cursor: pointer; outline: none; min-width: 54px;
        }
        .etb-select:focus { border-color: #93c5fd; }
        .etb-select:disabled { opacity: 0.4; cursor: not-allowed; }
        .etb-color-wrap { position: relative; display: inline-flex; }
        .color-picker-popover {
          position: absolute; bottom: calc(100% + 6px); left: 0; background: #fff;
          border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12); z-index: 1000; width: 176px;
        }
        .color-picker-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; margin-bottom: 8px; }
        .color-swatch { width: 32px; height: 22px; border-radius: 4px; cursor: pointer; transition: transform 0.1s, box-shadow 0.1s; border: 1px solid rgba(0,0,0,0.1); }
        .color-swatch:hover { transform: scale(1.12); box-shadow: 0 2px 6px rgba(0,0,0,0.18); }
        .color-picker-custom { display: flex; align-items: center; gap: 6px; border-top: 1px solid #f1f5f9; padding-top: 8px; }
        .color-picker-custom-label { font-size: 11px; color: #94a3b8; flex-shrink: 0; }
        .color-picker-native { width: 0; height: 0; opacity: 0; position: absolute; }
        .color-picker-custom-btn {
          font-size: 11px; padding: 3px 8px; border: 1px solid #e2e8f0;
          border-radius: 4px; background: #fff; cursor: pointer; color: #374151;
        }
        .color-picker-custom-btn:hover { background: #f1f5f9; }
        .img-dialog-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.4);
          z-index: 2000; display: flex; align-items: center; justify-content: center;
        }
        .img-dialog {
          background: #fff; border-radius: 10px; width: 380px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18); overflow: hidden;
        }
        .img-dialog-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 16px 0; font-weight: 600; font-size: 15px; color: #1e293b;
        }
        .img-dialog-close {
          background: none; border: none; cursor: pointer; color: #64748b; display: flex;
        }
        .img-dialog-tabs { display: flex; border-bottom: 1px solid #e2e8f0; padding: 0 16px; margin-top: 10px; }
        .img-dialog-tab {
          padding: 8px 14px; font-size: 13px; border: none; background: none;
          cursor: pointer; color: #64748b; border-bottom: 2px solid transparent; margin-bottom: -1px;
        }
        .img-dialog-tab.active { color: #0284c7; border-bottom-color: #0284c7; font-weight: 500; }
        .img-dialog-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
        .img-dialog-input {
          width: 100%; padding: 8px 10px; font-size: 13px; border: 1px solid #e2e8f0;
          border-radius: 6px; outline: none; box-sizing: border-box;
        }
        .img-dialog-input:focus { border-color: #93c5fd; }
        .img-upload-area {
          border: 2px dashed #e2e8f0; border-radius: 8px; min-height: 110px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: border-color 0.15s;
        }
        .img-upload-area:hover { border-color: #93c5fd; }
        .img-upload-area--has-file { border-style: solid; border-color: #bfdbfe; }
        .img-upload-placeholder { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #94a3b8; font-size: 13px; }
        .img-upload-hint { font-size: 11px; }
        .img-upload-preview { max-width: 100%; max-height: 110px; object-fit: contain; }
        .img-upload-spinner { animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .img-dialog-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #f1f5f9; }
        .img-dialog-cancel {
          padding: 7px 14px; border: 1px solid #e2e8f0; border-radius: 6px;
          background: #fff; color: #374151; font-size: 13px; cursor: pointer;
        }
        .img-dialog-insert {
          padding: 7px 14px; border: none; border-radius: 6px;
          background: #0284c7; color: #fff; font-size: 13px; cursor: pointer;
        }
        .img-dialog-insert:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
